import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Plus, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useHostCandidates } from "@/hooks/useHostCandidates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn, displayName } from "@/lib/utils";
import {
  EventGroupFormRow,
  earliestGroupIndex,
  toNumber,
  validateEventGroups,
} from "@/lib/eventGroups";

interface Props {
  eventId?: string;
  groups: EventGroupFormRow[];
  eventCapacity: number;
  eventStartTime: string;
  /** Event-level hosts, used to pre-fill the first two waves. */
  eventHostId?: string;
  eventSecondaryHostId?: string;
  onChange: (groups: EventGroupFormRow[]) => void;
  /** Reports the current blocking problem (or null) so the form can refuse to save. */
  onValidityChange: (error: string | null) => void;
}

const WaveHostPicker = ({
  label,
  value,
  exclude,
  onChange,
}: {
  label: string;
  value: string;
  exclude?: string;
  onChange: (id: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const { data: hosts, isLoading } = useHostCandidates([value, exclude]);
  const options = (hosts ?? []).filter((h) => h.id !== exclude);
  const selected = options.find((h) => h.id === value);
  const name = (h: { first_name: string | null; last_name: string | null }) =>
    displayName(h.first_name ?? undefined, h.last_name ?? undefined);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <Label className="text-xs">{label}</Label>
        {value && (
          <button type="button" className="text-xs text-muted-foreground underline" onClick={() => onChange("")}>
            Clear
          </button>
        )}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" className="w-full justify-between bg-card font-normal">
            <span className={cn(!selected && "text-muted-foreground")}>
              {selected ? name(selected) : isLoading && value ? "Loading..." : "None"}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-popover" align="start">
          <Command>
            <CommandInput placeholder="Type host name..." />
            <CommandList>
              <CommandEmpty>{isLoading ? "Loading hosts..." : "No host found."}</CommandEmpty>
              <CommandGroup>
                {options.map((h) => (
                  <CommandItem key={h.id} value={`${name(h)} ${h.id}`} onSelect={() => { onChange(h.id); setOpen(false); }}>
                    <Check className={cn("mr-2 h-4 w-4", value === h.id ? "opacity-100" : "opacity-0")} />
                    {name(h)}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const EventGroupsSection = ({ eventId, groups, eventCapacity, eventStartTime, eventHostId, eventSecondaryHostId, onChange, onValidityChange }: Props) => {
  // Confirmed seats per wave, plus seats not yet in any wave (they join the earliest wave on save).
  const { data: booked } = useQuery({
    queryKey: ["admin-event-group-booked", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tickets")
        .select("group_id, quantity")
        .eq("event_id", eventId!)
        .eq("status", "confirmed");
      if (error) throw error;
      const byGroup: Record<string, number> = {};
      let unassigned = 0;
      (data ?? []).forEach((t) => {
        if (t.group_id) byGroup[t.group_id] = (byGroup[t.group_id] ?? 0) + t.quantity;
        else unassigned += t.quantity;
      });
      return { byGroup, unassigned };
    },
  });
  const byGroup = booked?.byGroup ?? {};
  const unassigned = booked?.unassigned ?? 0;
  const first = earliestGroupIndex(groups);

  const error = useMemo(
    () => validateEventGroups(groups, eventCapacity, byGroup, unassigned),
    [groups, eventCapacity, byGroup, unassigned],
  );
  useEffect(() => onValidityChange(error), [error, onValidityChange]);

  const update = (i: number, patch: Partial<EventGroupFormRow>) =>
    onChange(groups.map((g, idx) => (idx === i ? { ...g, ...patch } : g)));

  const addWave = () => {
    const last = groups[groups.length - 1];
    const used = groups.reduce((s, g) => s + toNumber(g.capacity), 0);
    // Pre-fill the first two waves with the event's hosts; later waves start blank.
    const hostId =
      groups.length === 0 ? eventHostId ?? "" :
      groups.length === 1 ? eventSecondaryHostId ?? "" : "";
    const secondaryHostId = "";
    onChange([
      ...groups,
      {
        name: groups.length === 0 ? "Group 1" : `Group ${groups.length + 1}`,
        startTime: last?.startTime || eventStartTime || "",
        endTime: "",
        capacity: Math.max(0, eventCapacity - used),
        hostId,
        secondaryHostId,
        meetingVenue: "",
        whatsappGroup: "",
        displayOrder: groups.length,
      },
    ]);
  };

  const sum = groups.reduce((s, g) => s + toNumber(g.capacity), 0);
  const sumMatches = sum === eventCapacity;

  return (
    <div className="rounded-lg border p-4 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Label className="text-base">Departure waves</Label>
          <p className="text-xs text-muted-foreground mt-1">
            Optional. Split this event into groups with their own time, host and meeting point. Wave capacities must add up to the event capacity — raise the capacity above as you add a wave.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addWave}>
          <Plus className="w-4 h-4 mr-1" /> Add wave
        </Button>
      </div>

      {groups.length > 0 && unassigned > 0 && (
        <p className="text-xs text-muted-foreground">
          {unassigned} existing booking{unassigned === 1 ? "" : "s"} will join the earliest wave when you save.
        </p>
      )}

      {groups.map((g, i) => {
        const bookedHere = (g.id ? byGroup[g.id] ?? 0 : 0) + (i === first ? unassigned : 0);
        const tooSmall = toNumber(g.capacity) < bookedHere;
        return (
          <div key={g.id ?? `new-${i}`} className="rounded-md border bg-muted/30 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Wave {i + 1}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={bookedHere > 0}
                title={bookedHere > 0 ? "Move its attendees to another wave first" : "Remove wave"}
                onClick={() => onChange(groups.filter((_, idx) => idx !== i))}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name</Label>
                <Input value={g.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="e.g. 2:30 group" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Capacity</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    value={g.capacity}
                    onChange={(e) => update(i, { capacity: e.target.value })}
                    className={tooSmall ? "border-destructive" : ""}
                  />
                  <span className={cn("text-xs whitespace-nowrap", tooSmall ? "text-destructive" : "text-muted-foreground")}>
                    {bookedHere} booked
                  </span>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Start time</Label>
                <Input type="datetime-local" value={g.startTime} onChange={(e) => update(i, { startTime: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">End time (optional)</Label>
                <Input type="datetime-local" value={g.endTime} onChange={(e) => update(i, { endTime: e.target.value })} />
              </div>
              <WaveHostPicker label="Host" value={g.hostId} exclude={g.secondaryHostId} onChange={(id) => update(i, { hostId: id })} />
              <WaveHostPicker label="Second host (optional)" value={g.secondaryHostId} exclude={g.hostId} onChange={(id) => update(i, { secondaryHostId: id })} />
              <div className="space-y-1">
                <Label className="text-xs">Meeting venue (optional)</Label>
                <Input value={g.meetingVenue} onChange={(e) => update(i, { meetingVenue: e.target.value })} placeholder="Blank = event venue" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">WhatsApp link (optional)</Label>
                <Input value={g.whatsappGroup} onChange={(e) => update(i, { whatsappGroup: e.target.value })} placeholder="Blank = event WhatsApp group" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Display order</Label>
                <Input type="number" value={g.displayOrder} onChange={(e) => update(i, { displayOrder: toNumber(e.target.value) })} />
              </div>
            </div>
          </div>
        );
      })}

      {groups.length > 0 && (
        <div className="space-y-1">
          <p className={cn("text-sm font-medium", sumMatches ? "text-success" : "text-destructive")}>
            {groups.map((g) => toNumber(g.capacity)).join(" + ")} = {sum} of {eventCapacity}
          </p>
          <p className="text-xs text-muted-foreground">
            Blank meeting venue or WhatsApp link uses the event's own, so later event edits still apply.
          </p>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default EventGroupsSection;
